import { useState, useEffect, useRef } from 'react'
import { Image, Search, ChevronDown, X } from 'lucide-react'
import { useAuth } from '../../../context/AuthContext'
import './ImagePicker.css'

/**
 * ImagePicker — Dropdown search for Cloudflare Images.
 * 
 * Props:
 *   value        - current thumbnail URL string
 *   onChange      - called with new URL string
 *   placeholder   - input placeholder text
 */
export default function ImagePicker({ value, onChange, placeholder }) {
  const { api } = useAuth()
  const [images, setImages] = useState([])
  const [loading, setLoading] = useState(false)
  const [loaded, setLoaded] = useState(false)
  const [search, setSearch] = useState('')
  const [open, setOpen] = useState(false)
  const ref = useRef(null)

  // Close on outside click
  useEffect(() => {
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  async function loadImages() {
    if (loaded) return
    setLoading(true)
    try {
      const data = await api('/images?per_page=100&sort_order=desc')
      const result = data.result || data
      setImages(result.images || [])
    } catch (e) {
      console.error('ImagePicker load failed:', e)
      setImages([])
    }
    setLoading(false)
    setLoaded(true)
  }

  function handleOpen() {
    setOpen(true)
    if (!loaded) loadImages()
  }

  function selectImage(img) {
    const url = img.variants?.find(v => v.endsWith('/public')) || img.variants?.[0] || ''
    if (url) onChange(url)
    setOpen(false)
    setSearch('')
  }

  function clearSelection() {
    onChange('')
  }

  const filtered = search
    ? images.filter(img =>
        (img.filename || '').toLowerCase().includes(search.toLowerCase()) ||
        (img.id || '').toLowerCase().includes(search.toLowerCase())
      )
    : images

  return (
    <div className="img-picker" ref={ref}>
      <div className="img-picker-input-row">
        <input
          type="text"
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder={placeholder || 'https://...'}
          className="img-picker-url"
        />
        <button
          type="button"
          className="img-picker-browse"
          onClick={handleOpen}
          title="Browse Cloudflare Images"
        >
          <Image size={14} />
          <ChevronDown size={12} className={open ? 'rotated' : ''} />
        </button>
        {value && (
          <button type="button" className="img-picker-clear" onClick={clearSelection} title="Clear">
            <X size={12} />
          </button>
        )}
      </div>

      {/* Thumbnail preview */}
      {value && (
        <div className="img-picker-preview">
          <img src={value} alt="Thumbnail preview" />
        </div>
      )}

      {/* Dropdown */}
      {open && (
        <div className="img-picker-dropdown">
          <div className="img-picker-search">
            <Search size={12} />
            <input
              type="text"
              placeholder="Search images by filename..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              autoFocus
            />
          </div>
          <div className="img-picker-list">
            {loading ? (
              <div className="img-picker-empty">Loading images…</div>
            ) : filtered.length === 0 ? (
              <div className="img-picker-empty">
                {search ? 'No images match your search' : 'No images found'}
              </div>
            ) : (
              filtered.slice(0, 50).map(img => {
                const thumbUrl = img.variants?.[0] || ''
                const publicUrl = img.variants?.find(v => v.endsWith('/public')) || thumbUrl
                const isActive = value === publicUrl
                return (
                  <div
                    key={img.id}
                    className={`img-picker-item ${isActive ? 'active' : ''}`}
                    onClick={() => selectImage(img)}
                  >
                    <div className="img-picker-thumb">
                      {thumbUrl ? (
                        <img src={thumbUrl} alt="" loading="lazy" />
                      ) : (
                        <Image size={20} />
                      )}
                    </div>
                    <div className="img-picker-info">
                      <div className="img-picker-name">{img.filename || img.id.substring(0, 20)}</div>
                      <div className="img-picker-meta">
                        {img.uploaded ? new Date(img.uploaded).toLocaleDateString() : ''}
                      </div>
                    </div>
                  </div>
                )
              })
            )}
          </div>
        </div>
      )}
    </div>
  )
}
